/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import DynamicFieldsModel from '@linc-technologies/linced-common/models/dynamic-field/base-model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array, fragment } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import CountryCode from '@linc-technologies/linced-common/enums/country-code';
import Feature from '@linc-technologies/linced-common/enums/feature';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Options from '@linc-technologies/linced-common/enums/options';
import type Address from '@linc-technologies/linced-common/models/address';
import type OptionsService from '@linc-technologies/linced-common/services/options';
import { schoolOptionAlias } from '@linc-technologies/linced-common/utils/options';

// Proto: schools.School.
export default class School extends DynamicFieldsModel {
  declare [Type]: 'school';

  /** Services **/
  @service
  declare options: OptionsService;

  // option gives the value of a school option on this school (not its
  // default), or undefined. The options service value(key) gives the default
  // when the session school has no value.
  option(key: string): unknown {
    return this.options.schoolGet(this, key);
  }

  /** Attributes **/
  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // countryCode contains an enum of the ISO "ALPHA-2" code.
  @attr('number', { defaultValue: 0 })
  declare countryCode: number;

  // name is the Name of the School.
  @attr('string', { defaultValue: '' })
  declare name: string;

  // domain stores the school's official website address for use in documents
  // and reports.
  @attr('string', { defaultValue: '' })
  declare domain: string;

  // fax is the School Fax Number on which the public can send facsimile
  // messages.
  @attr('string', { defaultValue: '' })
  declare fax: string;

  // email is the School Email address on which the public can send e-mails
  // to. This email address will be used to send replies from txt messages
  // and other admin notification emails.
  @attr('string', { defaultValue: '' })
  declare emailAddress: string;

  // phone is the School Phone Number on which the public can reach the
  // school from.
  @attr('string', { defaultValue: '' })
  declare phone: string;

  // physicalAddress is the physical address of the school.
  @fragment('address')
  declare physicalAddress: Address | null;

  // postalAddress is the address the school receives mail at.
  @fragment('address')
  declare postalAddress: Address | null;

  // jurisdiction specifies the jurisdictional education authority the school
  // complies reporting to.
  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  // allowedJurisdictions specifies the jurisdictions the school is allowed
  // to assign to learners.
  @array('number')
  declare allowedJurisdictions: StatefulArray<number>;

  // jurisdictionalId is the school's unique identifier assigned by the
  // jurisdictional authority (i.e. NZ MOE).
  @attr('string', { defaultValue: '' })
  declare jurisdictionalId: string;

  // externalId is an external identifier distinct from the jurisdictional ID
  // (i.e. SIF RefID).
  @attr('string', { defaultValue: '' })
  declare externalId: string;

  // logoMediaId is the Media record id which contains the school's logo
  @attr('string', { defaultValue: '' })
  declare logoMediaId: string;

  @alias('logoMediaId')
  declare logoMedia: string | undefined;

  @alias('logoMediaId')
  declare mediaId: string | undefined; // required by AvatarImage component

  /**
   * options contains dynamically assigned fields.
   * Note:
   * - Handled by the dynamic fields mixin
   * options: attr(),
   */
  // optionSetIds contain a list of option set unique identifiers that allow
  // building the School's Option Schema.
  @array('string')
  declare optionSetIds: StatefulArray<string>;

  // featuresAvailable determines which Features are potentially available to
  // this School.
  @array('number')
  declare featuresAvailable: StatefulArray<number>;

  // featuresEnabled determines which Features are enabled for this School.
  @array('number')
  declare featuresEnabled: StatefulArray<number>;

  // supportPersonIds is a list of Person IDs for staff members who have
  // permission to create support tickets.
  @array('string')
  declare supportPersonIds: StatefulArray<string>;

  // isCharter indicates the school is a Charter school
  // Applies to NZMOE schools only
  @attr('boolean', { defaultValue: false })
  declare isCharter: boolean;

  // isPrivate indicates the school is a Private school
  // Applies to NZMOE schools only
  @attr('boolean', { defaultValue: false })
  declare isPrivate: boolean;

  // communityFeedCommentsEnabled specifies if this school has enabled comments
  // on the community feed.
  @schoolOptionAlias(Options.COMMUNITY_FEED_COMMENTS)
  declare communityFeedCommentsEnabled: boolean | undefined;

  // warehouseStationeryJson returns a blob of json to school stationery links,
  // if available.
  @schoolOptionAlias(Options.HERO_WAREHOUSE_STATIONERY_JSON)
  declare warehouseStationeryJson: string | undefined;

  // Notifications options
  @schoolOptionAlias(Options.NOTIFICATIONS_APP_ENABLED)
  declare pushNotificationsGlobalEnabled: boolean | undefined;

  @schoolOptionAlias(Options.NOTIFICATIONS_APP_POSTS)
  declare pushNotificationsPostsEnabled: boolean | undefined;

  @schoolOptionAlias(Options.NOTIFICATIONS_APP_COMMUNITY_NOTICES)
  declare pushNotificationsCommunityNoticesEnabled: boolean | undefined;

  @schoolOptionAlias(Options.NOTIFICATIONS_EMAIL_ENABLED)
  declare emailNotificationsGlobalEnabled: boolean | undefined;

  @schoolOptionAlias(Options.NOTIFICATIONS_EMAIL_POSTS)
  declare emailNotificationsPostsEnabled: boolean | undefined;

  @schoolOptionAlias(Options.NOTIFICATIONS_EMAIL_COMMUNITY_NOTICES)
  declare emailNotificationsCommunityNoticesEnabled: boolean | undefined;

  // sidekickEnabled specifies if Sidekick is enabled for this school.
  @schoolOptionAlias(Options.SIDEKICK_ENABLED)
  declare sidekickEnabled: boolean | undefined;

  // linkedGoalsEnabled specifies whether goal linking (Topics) is enabled.
  @schoolOptionAlias(Options.LINKED_GOALS_ENABLED)
  declare linkedGoalsEnabled: boolean | undefined;

  // controlPanelEnabled specifies if the landing page for a school user should be the control panel.
  @schoolOptionAlias(Options.CONTROL_PANEL_ENABLED)
  declare controlPanelEnabled: boolean | undefined;

  @schoolOptionAlias(Options.REPORT_SNAPSHOTS_ENABLED)
  declare reportSnapshotsEnabled: boolean | undefined;

  // teRitoEnabled specifies if Te Rito is enabled for this school.
  @schoolOptionAlias(Options.TE_RITO_ENABLED)
  declare teRitoEnabled: boolean | undefined;

  // eventsEnabled specifies if calendar and excursions are enabled for this school.
  @schoolOptionAlias(Options.EVENTS_ENABLED)
  declare eventsEnabled: boolean | undefined;

  // nswDoeProhibitedFinancialLanguage stores the comma-separated list of
  // financial terms that NSW DoE schools are prohibited from using in
  // parent-facing communications, as per the NSW DoE Voluntary School
  // Contributions Policy (SMOS-012).
  @schoolOptionAlias(Options.NSW_DOE_PROHIBITED_FINANCIAL_LANGUAGE)
  declare nswDoeProhibitedFinancialLanguage: string | undefined;

  /** Methods **/
  // featureAvailable checks to see whether the specified feature is available
  // for this school.
  featureAvailable(feature: number): boolean {
    return (
      this.featureEnabled(feature) || this.featuresAvailable.includes(feature)
    );
  }

  // featureEnabled checks to see whether the specified feature is enabled for
  // this school.
  featureEnabled(feature: number): boolean {
    return this.featuresEnabled.includes(feature);
  }

  // jurisdictionAvailable checks to see whether the specified jurisdiction is
  // available for this school.
  jurisdictionAvailable(jurisdiction: number): boolean {
    return this.allowedJurisdictions.includes(jurisdiction);
  }

  // countryName gets the name of the country based on the country code
  @computed('countryCode')
  get countryName() {
    // A country code of 0 (the Go zero value) is not set.
    if (this.countryCode) {
      // TS-BUG: an unknown country code throws here.
      return CountryCode.values.find(
        (country) => country.value === this.countryCode,
      )!.name;
    }
    return '';
  }

  // snapshotsWording displays labels for snapshots in UI based on jurisdiction
  @computed('jurisdiction')
  get snapshotsWording() {
    // As a side note, wording does not guarantee those specific types of snapshots will be displayed.
    // Which snapshot types are displayed can be found in designated routes, i.e. at the time of writing,
    // NSW sees only on-demand ones that they generate themselves as "bi-annual"
    switch (this.jurisdiction) {
      case Jurisdiction.AU_NSW:
        return 'Semester Reports';
      case Jurisdiction.NZ_MOE:
        return 'Bi-annual Reports';
      default:
        return 'Termly Reports';
    }
  }

  //// Parent app config (theming, links, etc).
  // appTheme is the theme selected by the school.
  @schoolOptionAlias(Options.APP_THEME)
  declare appTheme: string | undefined;

  // showLogo determines whether to show the school's logo on the parent app
  // homepage.
  @schoolOptionAlias(Options.APP_SHOW_LOGO)
  declare showLogo: boolean | undefined;

  // logoSize determines which size of logo to display (if enabled above).
  @schoolOptionAlias(Options.APP_LOGO_SIZE)
  declare logoSize: unknown;

  // googleCalendar specifies the school's public Google Calendar ID.
  @schoolOptionAlias(Options.APP_LINKS_GOOGLE_CALENDAR)
  declare googleCalendar: string | undefined;

  // _absenceReportingEnabled specifies whether the school wishes to show the
  // "Report an Absence" option for parents.
  @schoolOptionAlias(Options.APP_FEATURE_ABSENCE_REPORTING)
  declare _absenceReportingEnabled: boolean | undefined;

  // absenceReportingEnabled applies a default/fallback to
  // _absenceReportingEnabled to make it true if the option isn't saved.
  @computed('_absenceReportingEnabled')
  get absenceReportingEnabled() {
    if (isEmpty(this._absenceReportingEnabled)) {
      // School hasn't set/saved the option.  Default to true.
      return true;
    }
    return this._absenceReportingEnabled;
  }

  // Customised links
  @schoolOptionAlias(Options.APP_LINKS_SCHOOL_WEBSITE)
  declare webSiteLink: string | undefined;

  @schoolOptionAlias(Options.APP_LINKS_POLICIES)
  declare policiesLink: string | undefined;

  @schoolOptionAlias(Options.APP_LINKS_SHOP)
  declare shopLink: string | undefined;

  @schoolOptionAlias(Options.APP_LINKS_UNIFORM)
  declare uniformLink: string | undefined;

  @schoolOptionAlias(Options.APP_LINKS_NEWS)
  declare newsLink: string | undefined;

  @schoolOptionAlias(Options.APP_LINKS_PORTAL)
  declare portalLink: string | undefined;

  @schoolOptionAlias(Options.APP_LINKS_FOOD)
  declare foodLink: string | undefined;

  @schoolOptionAlias(Options.APP_LINKS_LIBRARY)
  declare libraryLink: string | undefined;

  // Social links
  @schoolOptionAlias(Options.APP_LINKS_HAIL)
  declare hailLink: string | undefined;

  @schoolOptionAlias(Options.APP_LINKS_FACEBOOK)
  declare facebookLink: string | undefined;

  @schoolOptionAlias(Options.APP_LINKS_TWITTER)
  declare twitterLink: string | undefined;

  @schoolOptionAlias(Options.APP_LINKS_INSTAGRAM)
  declare instagramLink: string | undefined;

  @computed('featuresAvailable.[]')
  get heroDuoAvailable() {
    return this.featureAvailable(Feature.DUO);
  }

  @computed(
    'featuresAvailable.[]',
    'heroStarterPackEnabled',
    'administrationExtensionKitAvailable',
  )
  get heroSuiteAvailable() {
    if (this.heroStarterPackEnabled) {
      return this.administrationExtensionKitAvailable;
    }
    return this.featureAvailable(Feature.SUITE);
  }

  @computed('featuresAvailable.[]')
  get pupilBillingAvailable() {
    return this.featureAvailable(Feature.PUPIL_BILLING);
  }

  @computed('featuresEnabled.[]')
  get heroDuoEnabled() {
    return this.featureEnabled(Feature.DUO);
  }

  @computed('featuresEnabled.[]')
  get heroPremierEnabled() {
    return this.featureEnabled(Feature.PREMIER);
  }

  @computed('featuresEnabled.[]', 'heroStarterPackEnabled')
  get heroSuiteEnabled() {
    if (this.heroStarterPackEnabled) {
      return this.administrationExtensionKitEnabled;
    }
    return this.featureEnabled(Feature.SUITE);
  }

  @computed('featuresEnabled.[]')
  get pupilBillingEnabled() {
    return this.featureEnabled(Feature.PUPIL_BILLING);
  }

  // requiresFinancialLanguageValidation returns true when the school is an
  // NSW DoE school with Pupil Billing enabled, requiring validation of
  // parent-facing finance free-text fields against the prohibited language
  // list (SMOS-012).
  @computed('jurisdiction', 'featuresEnabled.[]')
  get requiresFinancialLanguageValidation() {
    return (
      this.jurisdiction === Jurisdiction.AU_NSW &&
      this.featureEnabled(Feature.PUPIL_BILLING)
    );
  }

  @computed('featuresEnabled.[]')
  get multiJurisdictionalEnabled() {
    return this.featureEnabled(Feature.MULTI_JURISDICTIONAL);
  }

  // Base pack for NSW and NZ schools using the pack system
  @computed('featuresEnabled.[]')
  get heroStarterPackEnabled() {
    return this.featureEnabled(Feature.STARTER_PACK);
  }

  // HERO Feature Packs
  // Available
  @computed('featuresAvailable.[]', 'heroStarterPackEnabled')
  get pagesPowerUpAvailable() {
    if (!this.heroStarterPackEnabled) {
      return true;
    }
    return this.featureAvailable(Feature.PAGES_POWER_UP);
  }

  @computed('featuresAvailable.[]', 'heroStarterPackEnabled')
  get milestonesPowerUpAvailable() {
    if (!this.heroStarterPackEnabled) {
      return true;
    }
    return this.featureAvailable(Feature.MILESTONES_POWER_UP);
  }

  @computed('featuresAvailable.[]', 'heroStarterPackEnabled')
  get goalsPowerUpAvailable() {
    if (!this.heroStarterPackEnabled) {
      return true;
    }
    return this.featureAvailable(Feature.GOALS_POWER_UP);
  }

  @computed('featuresAvailable.[]', 'heroStarterPackEnabled')
  get reportingPowerUpAvailable() {
    if (!this.heroStarterPackEnabled) {
      return true;
    }
    return this.featureAvailable(Feature.REPORTING_POWER_UP);
  }

  @computed('featuresAvailable.[]', 'heroStarterPackEnabled')
  get communicationsExtensionKitAvailable() {
    if (!this.heroStarterPackEnabled) {
      return true;
    }
    return this.featureAvailable(Feature.COMMUNICATIONS_KIT);
  }

  @computed('featuresAvailable.[]', 'heroStarterPackEnabled')
  get administrationExtensionKitAvailable() {
    if (!this.heroStarterPackEnabled) {
      return true;
    }
    return this.featureAvailable(Feature.ADMINISTRATION_KIT);
  }

  // Enabled
  @computed('featuresEnabled.[]', 'heroStarterPackEnabled')
  get pagesPowerUpEnabled() {
    if (!this.heroStarterPackEnabled) {
      return true;
    }
    return this.featureEnabled(Feature.PAGES_POWER_UP);
  }

  @computed('featuresEnabled.[]', 'heroStarterPackEnabled')
  get milestonesPowerUpEnabled() {
    if (!this.heroStarterPackEnabled) {
      return true;
    }
    return this.featureEnabled(Feature.MILESTONES_POWER_UP);
  }

  @computed('featuresEnabled.[]', 'heroStarterPackEnabled')
  get goalsPowerUpEnabled() {
    if (!this.heroStarterPackEnabled) {
      return true;
    }
    return this.featureEnabled(Feature.GOALS_POWER_UP);
  }

  @computed('featuresEnabled.[]', 'heroStarterPackEnabled')
  get reportingPowerUpEnabled() {
    if (!this.heroStarterPackEnabled) {
      return true;
    }
    return this.featureEnabled(Feature.REPORTING_POWER_UP);
  }

  @computed('featuresEnabled.[]', 'heroStarterPackEnabled')
  get communicationsExtensionKitEnabled() {
    if (!this.heroStarterPackEnabled) {
      return true;
    }
    return this.featureEnabled(Feature.COMMUNICATIONS_KIT);
  }

  @computed('featuresEnabled.[]', 'heroStarterPackEnabled')
  get administrationExtensionKitEnabled() {
    if (!this.heroStarterPackEnabled) {
      return true;
    }
    return this.featureEnabled(Feature.ADMINISTRATION_KIT);
  }

  @computed(
    'heroPremierEnabled',
    'heroStarterPackEnabled',
    'administrationExtensionKitEnabled',
  )
  get absenceReportingAvailable() {
    if (this.heroStarterPackEnabled) {
      return this.administrationExtensionKitEnabled;
    }
    return this.heroPremierEnabled;
  }

  @computed(
    'heroPremierEnabled',
    'heroStarterPackEnabled',
    'communicationsExtensionKitEnabled',
  )
  get schoolBookingsEnabled() {
    if (this.heroStarterPackEnabled) {
      return this.communicationsExtensionKitEnabled;
    }
    return this.heroPremierEnabled;
  }

  // TODO: lazy load school logos where required
}
